import React, { useEffect, useRef, useState } from "react";
import "./MappingFilter.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/js/dist/dropdown";
import { Dropdown } from "bootstrap";
import AreaService from "../../services/areaService";
import { useOktaAuth } from "@okta/okta-react";
import { IFilterOptions, IMappingFilterProps } from "../../types/mappingFilterData";
import ApiService from "../../services/apiService";
import { useLocation, useNavigate } from "react-router-dom";
import InputWithIcon from "../Input/InputWithIcon";
import TagCard from "../TagCard/TagCard";

interface Area {
  id: string;
  partitionKey: string;
  name: string;
  parentId?: string | null;
}


const MappingFilter: React.FC<IMappingFilterProps> = ({ onChange, onInitialized }) => {
  const [rootAreas, setRootAreas] = useState<Area[]>([]);
  const [products, setProducts] = useState<Area[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [apiFilterOptions, setApiFilterOptions] = useState<IFilterOptions>({ apiStructure: [], managedByUser: [] });
  const [isRootAreasLoading, setIsRootAreasLoading] = useState(false);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [isAreasLoading, setIsAreasLoading] = useState(false);
  const [selectedLineBusinessName, setSelectedLineBusinessName] =
    useState<string>("All");
  const [selectedLineBusinessId, setSelectedLineBusinessId] =
    useState<string>("0");
  const [selectedProductName, setSelectedProductName] = useState<string>("All");
  const [selectedProductId, setSelectedProductId] = useState<string>("0");
  const [selectedAreaName, setSelectedAreaName] = useState<string>("All");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("0");
  const [selectedApiStructureId, setSelectedApiStructureId] = useState<string>("0");
  const [selectedApiStructureName, setSelectedApiStructureName] = useState<string>("All");
  const [selectedDeveloperProgram, setSelectedDeveloperProgram] = useState<string>("All");
  const [selectedManagedBy, setSelectedManagedBy] = useState<string>("All");
  const [nameFilter, setName] = useState<string>("");
  const [nameFilterDebounced, setNameFilterDebounced] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isModalOpen || !modalRef.current) {
      return;
    }
    const toggles = modalRef.current.querySelectorAll('[data-bs-toggle="dropdown"]');
    const dropdownInstances: Dropdown[] = [];
    toggles.forEach((toggle) => {
      const instance = Dropdown.getOrCreateInstance(toggle, {
        popperConfig: {
          modifiers: [
            { name: "flip", enabled: false },
            { name: "preventOverflow", enabled: false },
          ],
        },
      });
      dropdownInstances.push(instance);
    });
    return () => {
      dropdownInstances.forEach((instance) => instance.dispose());
    };
  }, [isModalOpen]);

  // Applied filters (shown as tags)
  const [appliedLineBusinessId, setAppliedLineBusinessId] = useState<string>("0");
  const [appliedLineBusinessName, setAppliedLineBusinessName] = useState<string>("All");
  const [appliedProductId, setAppliedProductId] = useState<string>("0");
  const [appliedProductName, setAppliedProductName] = useState<string>("All");
  const [appliedAreaId, setAppliedAreaId] = useState<string>("0");
  const [appliedAreaName, setAppliedAreaName] = useState<string>("All");
  const [appliedApiStructureId, setAppliedApiStructureId] = useState<string>("0");
  const [appliedApiStructureName, setAppliedApiStructureName] = useState<string>("All");
  const [appliedDeveloperProgram, setAppliedDeveloperProgram] = useState<string>("All");
  const [appliedManagedBy, setAppliedManagedBy] = useState<string>("All");
  
  const { authState } = useOktaAuth();
  const areaService = new AreaService();
  const apiService = new ApiService();
  const location = useLocation();
  const navigate = useNavigate();

  // Debounce effect for name filter - waits 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setNameFilterDebounced(nameFilter);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timer);
    };
  }, [nameFilter]);

  // Effect to trigger onChange when debounced value changes
  useEffect(() => {
    if (!isInitialized) return; // Don't trigger during initial load

    const filters = {
      areaId: getAreaIdFilter(selectedAreaId, selectedProductId, selectedLineBusinessId),
      apiStructure: selectedApiStructureId,
      developerProgram: selectedDeveloperProgram,
      managedBy: selectedManagedBy,
      name: nameFilterDebounced
    };
    
    onChange({
      areaId: filters.areaId,
      apiStructure: filters.apiStructure === "0" ? null : filters.apiStructure,
      managedBy: filters.managedBy === "All" ? null : filters.managedBy,
      developerProgram: filters.developerProgram === "All" ? null : filters.developerProgram,
      name: filters.name === "" ? null : filters.name
    });
    
    updateURLWithFilters(filters);
  }, [nameFilterDebounced]);

  // Helper function to update URL with current filters
  const updateURLWithFilters = (filters: {
    areaId: string;
    apiStructure: string;
    developerProgram: string;
    managedBy: string;
    name: string;
  }) => {
    const searchParams = new URLSearchParams(location.search);
    
    if (filters.areaId !== "0") {
      searchParams.set("areaId", filters.areaId);
    } else {
      searchParams.delete("areaId");
    }
    if (filters.apiStructure !== "0") {
      searchParams.set("apiStructure", filters.apiStructure);
    } else {
      searchParams.delete("apiStructure");
    }
    if (filters.developerProgram !== "All") {
      searchParams.set("developerProgram", filters.developerProgram);
    } else {
      searchParams.delete("developerProgram");
    }
    if (filters.managedBy !== "All") {
      searchParams.set("managedBy", filters.managedBy);
    } else {
      searchParams.delete("managedBy");
    }
    if (filters.name !== "") {
      searchParams.set("name", filters.name);
    } else {
      searchParams.delete("name");
    }

    searchParams.delete("page");

    // Persist filter state to page-scoped sessionStorage so it survives cross-page navigation
    try {
      const storageKey = `mapping-filter-state:${location.pathname}`;
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (e) { /* ignore */ }

    const newURL = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const currentURL = `${location.pathname}${location.search}`;
    if (newURL !== currentURL) {
      navigate(newURL, { replace: false });
    }
  };


  // Helper function to initialize area hierarchy from URL parameter
  const initializeAreaHierarchyFromURL = async (areaId: string, rootAreas: Area[]) => {
    if (areaId === "0") return;

    try {
      // First, check if the areaId is a root area (Line of Business)
      const rootArea = rootAreas.find(area => area.id === areaId);
      if (rootArea) {
        setSelectedLineBusinessId(areaId);
        setSelectedLineBusinessName(rootArea.name);
        setAppliedLineBusinessId(areaId);
        setAppliedLineBusinessName(rootArea.name);
        
        // Load products for this line of business
        setIsProductsLoading(true);
        try {
          const productData = (await areaService.getAreasByParentId(areaId, authState)) as Area[];
          setProducts(productData);
        } catch (error) {
          console.error("Error fetching products for root area:", error);
        } finally {
          setIsProductsLoading(false);
        }
        return;
      }

      // If not a root area, we need to find its hierarchy
      // This iterates through the hierarchy to find where the areaId belongs
      let foundHierarchy = false;
      
      for (const rootArea of rootAreas) {
        if (foundHierarchy) break;
        
        try {
          const products = (await areaService.getAreasByParentId(rootArea.id, authState)) as Area[];
          
          // Check if areaId is a product
          const product = products.find(p => p.id === areaId);
          if (product) {
            setSelectedLineBusinessId(rootArea.id);
            setSelectedLineBusinessName(rootArea.name);
            setSelectedProductId(areaId);
            setSelectedProductName(product.name);
            setAppliedLineBusinessId(rootArea.id);
            setAppliedLineBusinessName(rootArea.name);
            setAppliedProductId(areaId);
            setAppliedProductName(product.name);
            setProducts(products);
            
            // Load areas for this product
            setIsAreasLoading(true);
            try {
              const areaData = (await areaService.getAreasByParentId(areaId, authState)) as Area[];
              setAreas(areaData);
            } catch (error) {
              console.error("Error fetching areas for product:", error);
            } finally {
              setIsAreasLoading(false);
            }
            foundHierarchy = true;
            break;
          }

          // Check if areaId is an area (third level)
          for (const product of products) {
            if (foundHierarchy) break;
            
            try {
              const areas = (await areaService.getAreasByParentId(product.id, authState)) as Area[];
              const area = areas.find(a => a.id === areaId);
              if (area) {
                setSelectedLineBusinessId(rootArea.id);
                setSelectedLineBusinessName(rootArea.name);
                setSelectedProductId(product.id);
                setSelectedProductName(product.name);
                setSelectedAreaId(areaId);
                setSelectedAreaName(area.name);
                setAppliedLineBusinessId(rootArea.id);
                setAppliedLineBusinessName(rootArea.name);
                setAppliedProductId(product.id);
                setAppliedProductName(product.name);
                setAppliedAreaId(areaId);
                setAppliedAreaName(area.name);
                setProducts(products);
                setAreas(areas);
                foundHierarchy = true;
                break;
              }
            } catch (error) {
              console.error("Error fetching areas for product:", product.id, error);
            }
          }
        } catch (error) {
          console.error("Error fetching products for root area:", rootArea.id, error);
        }
      }

      // If we couldn't find the area in the hierarchy, reset to defaults but keep the areaId
      // This handles cases where the area might exist but we couldn't resolve it
      if (!foundHierarchy) {
        console.warn(`Could not resolve hierarchy for areaId: ${areaId}. Using defaults.`);
        setSelectedAreaId(areaId);
        setSelectedAreaName("Unknown Area");
      }
    } catch (error) {
      console.error("Error initializing area hierarchy from URL:", error);
      // Reset to defaults on error
      setSelectedLineBusinessId("0");
      setSelectedLineBusinessName("All");
      setSelectedProductId("0");
      setSelectedProductName("All");
      setSelectedAreaId("0");
      setSelectedAreaName("All");
    }
  };

  // Helper function to parse URL parameters, falling back to sessionStorage
  const parseURLParameters = () => {
    const searchParams = new URLSearchParams(location.search);
    const hasUrlFilters = searchParams.has("areaId") || searchParams.has("apiStructure") 
      || searchParams.has("developerProgram") || searchParams.has("managedBy") || searchParams.has("name");

    if (hasUrlFilters) {
      return {
        areaId: searchParams.get("areaId") || "0",
        apiStructure: searchParams.get("apiStructure") || "0",
        developerProgram: searchParams.get("developerProgram") || "All",
        managedBy: searchParams.get("managedBy") || "All",
        name: searchParams.get("name") || ""
      };
    }

    // Fall back to page-scoped sessionStorage when URL has no filter params
    try {
      const storageKey = `mapping-filter-state:${location.pathname}`;
      const stored = sessionStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch (e) { /* ignore */ }

    return {
      areaId: "0",
      apiStructure: "0",
      developerProgram: "All",
      managedBy: "All",
      name: ""
    };
  };

  
  // Sync filter state from URL on back/forward navigation
  useEffect(() => {
    if (!isInitialized) return; // Skip during initial mount (handled by init effect)

    const urlState = parseURLParameters();

    // Check if URL state differs from current applied state
    const currentAreaId = getAreaIdFilter(appliedAreaId, appliedProductId, appliedLineBusinessId);
    const urlAreaChanged = urlState.areaId !== currentAreaId;
    const urlApiStructureChanged = urlState.apiStructure !== appliedApiStructureId;
    const urlDeveloperProgramChanged = urlState.developerProgram !== appliedDeveloperProgram;
    const urlManagedByChanged = urlState.managedBy !== appliedManagedBy;
    const urlNameChanged = urlState.name !== nameFilterDebounced;

    if (!urlAreaChanged && !urlApiStructureChanged && !urlDeveloperProgramChanged && !urlManagedByChanged && !urlNameChanged) {
      return; // No change from navigation, skip to avoid loops
    }

    // Update API Structure state
    setSelectedApiStructureId(urlState.apiStructure);
    setSelectedApiStructureName(urlState.apiStructure === "0" ? "All" : urlState.apiStructure);
    setAppliedApiStructureId(urlState.apiStructure);
    setAppliedApiStructureName(urlState.apiStructure === "0" ? "All" : urlState.apiStructure);

    // Update Developer Program state
    setSelectedDeveloperProgram(urlState.developerProgram);
    setAppliedDeveloperProgram(urlState.developerProgram);

    // Update Managed By state
    setSelectedManagedBy(urlState.managedBy);
    setAppliedManagedBy(urlState.managedBy);

    // Update Name filter
    setName(urlState.name);
    setNameFilterDebounced(urlState.name);

    // Handle area hierarchy re-sync
    if (urlAreaChanged) {
      if (urlState.areaId === "0") {
        setSelectedLineBusinessId("0");
        setSelectedLineBusinessName("All");
        setSelectedProductId("0");
        setSelectedProductName("All");
        setSelectedAreaId("0");
        setSelectedAreaName("All");
        setAppliedLineBusinessId("0");
        setAppliedLineBusinessName("All");
        setAppliedProductId("0");
        setAppliedProductName("All");
        setAppliedAreaId("0");
        setAppliedAreaName("All");
        setProducts([]);
        setAreas([]);
      } else {
        // Re-resolve area hierarchy from URL
        initializeAreaHierarchyFromURL(urlState.areaId, rootAreas);
      }
    }

    // Notify parent with restored filters
    onChange({
      areaId: urlState.areaId,
      apiStructure: urlState.apiStructure === "0" ? null : urlState.apiStructure,
      developerProgram: urlState.developerProgram === "All" ? null : urlState.developerProgram,
      managedBy: urlState.managedBy === "All" ? null : urlState.managedBy,
      name: urlState.name === "" ? null : urlState.name
    });
  }, [location.search]);

  useEffect(() => {
    const fetchItems = async () => {
      setIsRootAreasLoading(true);
      try {
        const areas = (await areaService.getRootAreas(authState)) as Area[];
        setRootAreas(areas);

        const apiFilterOptions = await apiService.getFilterOptions(authState);
        setApiFilterOptions(apiFilterOptions);

        // Initialize filters from URL parameters
        const urlParams = parseURLParameters();
        console.log("Initializing from URL parameters:", urlParams);
        
        // Set API Structure filter
        setSelectedApiStructureId(urlParams.apiStructure);
        setSelectedApiStructureName(urlParams.apiStructure === "0" ? "All" : urlParams.apiStructure);
        setAppliedApiStructureId(urlParams.apiStructure);
        setAppliedApiStructureName(urlParams.apiStructure === "0" ? "All" : urlParams.apiStructure);
        
        // Set Developer Program filter
        setSelectedDeveloperProgram(urlParams.developerProgram);
        setAppliedDeveloperProgram(urlParams.developerProgram);
        
        // Set Managed By filter
        setSelectedManagedBy(urlParams.managedBy);
        setAppliedManagedBy(urlParams.managedBy);

        // Set Name filter
        setName(urlParams.name);

        // Handle area hierarchy initialization
        await initializeAreaHierarchyFromURL(urlParams.areaId, areas);

        // Prepare initial filter values
        const initialFilters = {
          areaId: urlParams.areaId,
          apiStructure: urlParams.apiStructure === "0" ? null : urlParams.apiStructure,
          developerProgram: urlParams.developerProgram === "All" ? null : urlParams.developerProgram,
          managedBy: urlParams.managedBy === "All" ? null : urlParams.managedBy,
          name: urlParams.name === "" ? null : urlParams.name
        };

        // Always call onChange with the initial filter values (even if they're defaults)
        // This ensures the parent component receives the filter state
        onChange(initialFilters);

        // Notify parent that initialization is complete
        if (onInitialized) {
          onInitialized(initialFilters);
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        setIsRootAreasLoading(false);
      }
    };

    fetchItems();
  }, []);

  const handleLineBusinessChange = async (id: string, name: string) => {
    closeAllDropdowns();
    setSelectedLineBusinessId(id);
    setSelectedLineBusinessName(name);
    setSelectedProductName("All");
    setSelectedProductId("0");
    setSelectedAreaName("All");
    setSelectedAreaId("0");

    if (id === "0") {
      setProducts([]);
      setAreas([]);
      return;
    }

    setIsProductsLoading(true);
    try {
      const data = (await areaService.getAreasByParentId(id, authState)) as Area[];
      setProducts(data);
    } catch (error) {
      console.error("Error fetching areas by parent ID:", error);
    } finally {
      setIsProductsLoading(false);
    }
  };

  const handleProductChange = async (id: string, name: string) => {
    closeAllDropdowns();
    setSelectedProductId(id);
    setSelectedProductName(name);
    setSelectedAreaName("All");
    setSelectedAreaId("0");

    if (id === "0") {
      setAreas([]);
      return;
    }

    setIsAreasLoading(true);
    try {
      const data = (await areaService.getAreasByParentId(id, authState)) as Area[];
      setAreas(data);
    } catch (error) {
      console.error("Error fetching areas by product ID:", error);
    } finally {
      setIsAreasLoading(false);
    }
  };

  const handleAreaChange = (id: string, name: string) => {
    closeAllDropdowns();
    setSelectedAreaId(id);
    setSelectedAreaName(name);
  };

  const handleApiStructureChange = (id: string, name: string) => {
    closeAllDropdowns();
    setSelectedApiStructureId(id);
    setSelectedApiStructureName(name);
  };

  const handleDeveloperProgramChange = (value: string) => {
    closeAllDropdowns();
    setSelectedDeveloperProgram(value);
  };

  const handleManagedByChange = (value: string) => {
    closeAllDropdowns();
    setSelectedManagedBy(value);
  };

  const handleNameFilterChange = (value: string) => {
    setName(value);
  };

  const handleRemoveFilter = (filterType: string) => {
    // Calculate new values before state updates
    let newAreaId = selectedAreaId;
    let newProductId = selectedProductId;
    let newLineBusinessId = selectedLineBusinessId;
    let newApiStructure = selectedApiStructureId;
    let newManagedBy = selectedManagedBy;
    let newDeveloperProgram = selectedDeveloperProgram;

    switch (filterType) {
      case 'lineBusiness':
        newLineBusinessId = "0";
        newProductId = "0";
        newAreaId = "0";
        setSelectedLineBusinessId("0");
        setSelectedLineBusinessName("All");
        setSelectedProductId("0");
        setSelectedProductName("All");
        setSelectedAreaId("0");
        setSelectedAreaName("All");
        setProducts([]);
        setAreas([]);
        setAppliedLineBusinessId("0");
        setAppliedLineBusinessName("All");
        setAppliedProductId("0");
        setAppliedProductName("All");
        setAppliedAreaId("0");
        setAppliedAreaName("All");
        break;
      case 'product':
        newProductId = "0";
        newAreaId = "0";
        setSelectedProductId("0");
        setSelectedProductName("All");
        setSelectedAreaId("0");
        setSelectedAreaName("All");
        setAreas([]);
        setAppliedProductId("0");
        setAppliedProductName("All");
        setAppliedAreaId("0");
        setAppliedAreaName("All");
        break;
      case 'area':
        newAreaId = "0";
        setSelectedAreaId("0");
        setSelectedAreaName("All");
        setAppliedAreaId("0");
        setAppliedAreaName("All");
        break;
      case 'apiStructure':
        newApiStructure = "0";
        setSelectedApiStructureId("0");
        setSelectedApiStructureName("All");
        setAppliedApiStructureId("0");
        setAppliedApiStructureName("All");
        break;
      case 'developerProgram':
        newDeveloperProgram = "All";
        setSelectedDeveloperProgram("All");
        setAppliedDeveloperProgram("All");
        break;
      case 'managedBy':
        newManagedBy = "All";
        setSelectedManagedBy("All");
        setAppliedManagedBy("All");
        break;
    }

    // Apply filters immediately after removal using the new calculated values
    const filters = {
      areaId: getAreaIdFilter(newAreaId, newProductId, newLineBusinessId),
      apiStructure: newApiStructure,
      managedBy: newManagedBy,
      developerProgram: newDeveloperProgram,
      name: nameFilterDebounced
    };
    
    onChange({
      areaId: filters.areaId,
      apiStructure: filters.apiStructure === "0" ? null : filters.apiStructure,
      managedBy: filters.managedBy === "All" ? null : filters.managedBy,
      developerProgram: filters.developerProgram === "All" ? null : filters.developerProgram,
      name: filters.name === "" ? null : filters.name
    });
    
    if (isInitialized) {
      updateURLWithFilters(filters);
    }
  };

  const handleApplyFilters = () => {
    setIsModalOpen(false);
    
    // Update applied filter values for display
    setAppliedLineBusinessId(selectedLineBusinessId);
    setAppliedLineBusinessName(selectedLineBusinessName);
    setAppliedProductId(selectedProductId);
    setAppliedProductName(selectedProductName);
    setAppliedAreaId(selectedAreaId);
    setAppliedAreaName(selectedAreaName);
    setAppliedApiStructureId(selectedApiStructureId);
    setAppliedApiStructureName(selectedApiStructureName);
    setAppliedDeveloperProgram(selectedDeveloperProgram);
    setAppliedManagedBy(selectedManagedBy);
    
    // Trigger filter update
    const filters = {
      areaId: getAreaIdFilter(selectedAreaId, selectedProductId, selectedLineBusinessId),
      apiStructure: selectedApiStructureId,
      managedBy: selectedManagedBy,
      developerProgram: selectedDeveloperProgram,
      name: nameFilterDebounced
    };
    
    onChange({
      areaId: filters.areaId,
      apiStructure: filters.apiStructure === "0" ? null : filters.apiStructure,
      managedBy: filters.managedBy === "All" ? null : filters.managedBy,
      developerProgram: filters.developerProgram === "All" ? null : filters.developerProgram,
      name: filters.name === "" ? null : filters.name
    });
    
    if (isInitialized) {
      updateURLWithFilters(filters);
    }
  };

  const handleResetFilters = () => {
    setSelectedLineBusinessId("0");
    setSelectedLineBusinessName("All");
    setSelectedProductId("0");
    setSelectedProductName("All");
    setSelectedAreaId("0");
    setSelectedAreaName("All");
    setSelectedApiStructureId("0");
    setSelectedApiStructureName("All");
    setSelectedDeveloperProgram("All");
    setSelectedManagedBy("All");
    setProducts([]);
    setAreas([]);
  };


  const closeAllDropdowns = () => {
    // Close all Bootstrap dropdowns in the modal
    const dropdowns = document.querySelectorAll('.filter-modal .dropdown-menu.show');
    dropdowns.forEach((dropdown) => {
      const toggle = dropdown.previousElementSibling as HTMLElement;
      if (toggle) {
        toggle.click(); // Simulate click to close dropdown
      }
    });
  };

  const getAreaIdFilter = (areaId?: string, productId?: string, businessId?: string) => {
    const currentAreaId = areaId !== undefined ? areaId : selectedAreaId;
    const currentProductId = productId !== undefined ? productId : selectedProductId;
    const currentBusinessId = businessId !== undefined ? businessId : selectedLineBusinessId;
    
    let filterId = "0";
    if (currentAreaId && currentAreaId !== "0") {
      filterId = currentAreaId;
    } else if (currentProductId && currentProductId !== "0") {
      filterId = currentProductId;
    } else if (currentBusinessId && currentBusinessId !== "0") {
      filterId = currentBusinessId;
    }
    return filterId;
  }

  return (
    <>
      <div className="filter-bar">
        <InputWithIcon
          value={nameFilter}
          onChange={handleNameFilterChange}
          placeholder="Search"
        />
        <button
          className="filter-button"
          onClick={() => setIsModalOpen(true)}
          title="Open filters"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 5.83333H17.5M5.83333 10H14.1667M8.33333 14.1667H11.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {/* Active Filter Tags */}
        <div className="active-filters">
          {appliedLineBusinessId !== "0" && (
            <TagCard
              label="Line of Business:"
              value={appliedLineBusinessName}
              onRemove={() => handleRemoveFilter('lineBusiness')}
            />
          )}
          {appliedProductId !== "0" && (
            <TagCard
              label="Product:"
              value={appliedProductName}
              onRemove={() => handleRemoveFilter('product')}
            />
          )}
          {appliedAreaId !== "0" && (
            <TagCard
              label="Area:"
              value={appliedAreaName}
              onRemove={() => handleRemoveFilter('area')}
            />
          )}
          {appliedApiStructureId !== "0" && (
            <TagCard
              label="API Structure:"
              value={appliedApiStructureName}
              onRemove={() => handleRemoveFilter('apiStructure')}
            />
          )}
          {appliedDeveloperProgram !== "All" && (
            <TagCard
              label="Developer Program:"
              value={appliedDeveloperProgram}
              onRemove={() => handleRemoveFilter('developerProgram')}
            />
          )}
          {appliedManagedBy !== "All" && (
            <TagCard
              label="Managed By:"
              value={appliedManagedBy}
              onRemove={() => handleRemoveFilter('managedBy')}
            />
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="filter-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="filter-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>Filters</h3>
              <button className="close-button" onClick={() => setIsModalOpen(false)}>
                ×
              </button>
            </div>
            
            <div className="filter-modal-body">
              <div className="filter-section">
                <div className="filter-row">
                  <div className="filter-group">
                    <div className={`label ${isRootAreasLoading ? "disabled" : ""}`}>
                      Line of Business
                    </div>
                    <button
                      className="dropdown form-control text-start"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={isRootAreasLoading}
                    >
                      {selectedLineBusinessName}
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleLineBusinessChange("0", "All")}
                        >
                          All
                        </button>
                      </li>
                      {rootAreas.map((rootArea) => (
                        <li key={rootArea.id}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleLineBusinessChange(rootArea.id, rootArea.name)}
                          >
                            {rootArea.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="filter-group">
                    <div className={`label ${isProductsLoading || products.length === 0 ? "disabled" : ""}`}>
                      Product
                    </div>
                    <button
                      className="dropdown form-control text-start"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={isProductsLoading || products.length === 0}
                    >
                      {selectedProductName}
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleProductChange("0", "All")}
                        >
                          All
                        </button>
                      </li>
                      {products.map((product) => (
                        <li key={product.id}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleProductChange(product.id, product.name)}
                          >
                            {product.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="filter-group">
                    <div className={`label ${isAreasLoading || areas.length === 0 ? "disabled" : ""}`}>
                      Area
                    </div>
                    <button
                      className="dropdown form-control text-start"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={isAreasLoading || areas.length === 0}
                    >
                      {selectedAreaName}
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleAreaChange("0", "All")}
                        >
                          All
                        </button>
                      </li>
                      {areas.map((area) => (
                        <li key={area.id}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleAreaChange(area.id, area.name)}
                          >
                            {area.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <div className="label">API Structure</div>
                    <button
                      className="dropdown form-control text-start"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {selectedApiStructureName}
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleApiStructureChange("0", "All")}
                        >
                          All
                        </button>
                      </li>
                      {apiFilterOptions.apiStructure.map((option) => (
                        <li key={option}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleApiStructureChange(option, option)}
                          >
                            {option}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="filter-group">
                    <div className="label">Developer Program</div>
                    <button
                      className="dropdown form-control text-start"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {selectedDeveloperProgram}
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleDeveloperProgramChange("All")}
                        >
                          All
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleDeveloperProgramChange("Yes")}
                        >
                          Yes
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => handleDeveloperProgramChange("No")}
                        >
                          No
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="filter-group">
                    <div className="label">Managed By</div>
                    <button
                      className="dropdown form-control text-start"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {selectedManagedBy}
                    </button>
                    <ul className="dropdown-menu">
                      {apiFilterOptions.managedByUser.map((option) => (
                        <li key={option}>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleManagedByChange(option)}
                          >
                            {option}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="filter-modal-footer">
              <button className="reset-button" onClick={handleResetFilters}>
                Reset
              </button>
              <button className="apply-button" onClick={handleApplyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MappingFilter;
